import deepmerge from 'deepmerge';
import type i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next, useTranslation } from 'react-i18next';
import type Maybe from 'true-myth/maybe';

import type {
    LanguageConfig,
    LanguageConfigMap,
    LanguageKey,
    TranslationOverrides,
} from '~/shared/internationalization';
import {
    availableI18nLanguages,
    defaultResources,
    i18nDefaultConfig,
    isAvailableLanguage,
    loadTranslationResource,
} from '~/shared/internationalization';
import defaultEnglishTranslation from '~/shared/internationalization/resources/en-US.json' with { type: 'json' };

export type InternationalizationDependencies = {
    readonly i18nProvider: Pick<typeof i18next, 'init' | 'use'>;
};

export type InternationalizationOptions = {
    readonly translationOverrides: Maybe<TranslationOverrides>;
};

export const useLanguage = (
    allowedLanguages: readonly LanguageKey[],
): {
    readonly language: LanguageConfig;
    readonly languageKey: LanguageKey;
    readonly languages: LanguageConfigMap;
} => {
    const {
        i18n: { resolvedLanguage },
    } = useTranslation();

    const filteredLanguages = Object.fromEntries(
        Object.entries(availableI18nLanguages).filter(([, config]) => allowedLanguages.includes(config.code)),
    ) as LanguageConfigMap;

    return isAvailableLanguage(resolvedLanguage) &&
        allowedLanguages.some((language) => language.includes(resolvedLanguage))
        ? {
              language: availableI18nLanguages[resolvedLanguage] ?? availableI18nLanguages['en-US'],
              languageKey: resolvedLanguage,
              languages: filteredLanguages,
          }
        : {
              language: availableI18nLanguages['en-US'],
              languageKey: 'en-US',
              languages: filteredLanguages,
          };
};

export const createInternationalization = async (
    dependencies: InternationalizationDependencies,
    options: InternationalizationOptions,
): Promise<void> => {
    const { i18nProvider } = dependencies;
    const { translationOverrides } = options;

    i18nProvider.use(LanguageDetector);
    i18nProvider.use(initReactI18next);
    i18nProvider.use(
        resourcesToBackend(async (language: string, _namespace: string) => {
            if (!isAvailableLanguage(language)) {
                return defaultEnglishTranslation;
            }

            const baseTranslation = await loadTranslationResource(language);

            return translationOverrides.match({
                Just: (overrides) => {
                    const languageOverride = overrides[language];

                    if (languageOverride === undefined) {
                        return baseTranslation;
                    }

                    return deepmerge(baseTranslation, languageOverride);
                },
                Nothing: () => baseTranslation,
            });
        }),
    );

    await i18nProvider.init({
        ...i18nDefaultConfig,
        resources: translationOverrides.mapOr(defaultResources, (overrides) =>
            deepmerge(defaultResources, {
                'de-DE': {
                    translation: overrides['de-DE'] ?? {},
                },
                'en-US': {
                    translation: overrides['en-US'] ?? {},
                },
            }),
        ),
    });
};
